import type { WhatsAppProvider } from './whatsapp';
import type { TenantFeature } from './platform';
import { REMOVED_TENANT_UI_FEATURES } from './platform';

export const WHATSAPP_PROVIDER_TENANT_FEATURES = {
  DIALOG360: 'whatsapp-360dialog',
  TWILIO: 'whatsapp-twilio',
  WAHA: 'whatsapp-waha',
  EVOHUB: 'whatsapp-evohub',
} as const satisfies Record<WhatsAppProvider, TenantFeature>;

export type WhatsAppProviderTenantFeature =
  (typeof WHATSAPP_PROVIDER_TENANT_FEATURES)[WhatsAppProvider];

export const WHATSAPP_PROVIDER_FEATURE_LIST = Object.values(
  WHATSAPP_PROVIDER_TENANT_FEATURES,
) as WhatsAppProviderTenantFeature[];

export function tenantHasWhatsAppModule(features: readonly TenantFeature[]): boolean {
  if (features.includes('whatsapp-integration')) {
    return true;
  }
  return WHATSAPP_PROVIDER_FEATURE_LIST.some(feature => features.includes(feature));
}

export function tenantHasWhatsAppProvider(
  features: readonly TenantFeature[],
  provider: WhatsAppProvider,
): boolean {
  if (features.includes('whatsapp-integration')) {
    return true;
  }
  return features.includes(WHATSAPP_PROVIDER_TENANT_FEATURES[provider]);
}

/** Convierte el flag legacy en los 4 proveedores para mostrar en el formulario de tenant. */
export function expandLegacyWhatsAppFeatures(features: readonly TenantFeature[]): TenantFeature[] {
  if (!features.includes('whatsapp-integration')) {
    return [...features];
  }
  const next = new Set(features);
  next.delete('whatsapp-integration');
  for (const feature of WHATSAPP_PROVIDER_FEATURE_LIST) {
    next.add(feature);
  }
  return [...next];
}

/** Normaliza features para persistencia: sin flags legacy ni comerciales obsoletos. */
export function sanitizeTenantFeaturesForStorage(features: readonly TenantFeature[]): TenantFeature[] {
  const removed = new Set<string>(REMOVED_TENANT_UI_FEATURES);
  return [...new Set(features.filter(feature => !removed.has(feature)))];
}

export function tenantFeaturesForForm(features: readonly TenantFeature[]): TenantFeature[] {
  return sanitizeTenantFeaturesForStorage(expandLegacyWhatsAppFeatures(features));
}
